export function getVideoSource(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    const path = (u.pathname || '').toLowerCase();

    // Direct MP4 hosted anywhere under sandia.gov
    if (isSandiaGovHost(host) && isMp4Path(path)) return 'sandia-mp4';

    if (host.includes('youtube.com') || host === 'youtu.be') return 'youtube';
    if (host.includes('vimeo.com')) return 'vimeo';
    if (host === 'digitalmedia.sandia.gov') return 'digitalmedia';
    if (host === 'digitalops.sandia.gov') return 'digitalops';

    return 'unknown';
  } catch {
    return null;
  }
}

function isMp4Path(pathname) {
  return /\.mp4$/i.test(pathname || '');
}

function isSandiaGovHost(host) {
  return host === 'sandia.gov' || host.endsWith('.sandia.gov');
}

export const SIZE_MAP = {
  Xsmall: { width: 430, height: 240 },
  Small: { width: 560, height: 315 },
  Medium: { width: 776, height: 436 },
  Large: { width: 1920, height: 1080 },
  SocialFeed: { width: '100%', height: '100%' },
};

export function getSizeAttrs(sizeKey = 'Medium') {
  return SIZE_MAP[sizeKey] || SIZE_MAP.Medium;
}

export function isDigitalMediaAllowed() {
  if (typeof window === 'undefined') return true;
  const host = String(window.location?.hostname || '').toLowerCase();
  if (
    host === 'www.sandia.gov' ||
    host === 'sandia.gov' ||
    host === 'www-qual.sandia.gov' ||
    host === 'www-dev.sandia.gov' ||
    host === 'sharepoint.sandia.gov'
  )
    return false;
  return true;
}

export function getUnsupportedUrlMessage(source) {
  if (source === 'digitalmedia' && !isDigitalMediaAllowed()) {
    return 'This video source isn’t available on this site.';
  }
  return 'Only YouTube, Vimeo, Sandia DigitalMedia, Sandia DigitalOps, and direct MP4s hosted on *.sandia.gov are supported.';
}

export function isSupportedVideoSource(source) {
  if (source === 'digitalmedia' && !isDigitalMediaAllowed()) return false;
  return (
    source === 'youtube' ||
    source === 'vimeo' ||
    source === 'digitalmedia' ||
    source === 'digitalops' ||
    source === 'sandia-mp4' ||
    source === 'sharepoint-mp4'
  );
}

export function normalizeVideoUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return '';

  const withScheme = /^www\./i.test(raw) ? `https://${raw}` : raw;

  try {
    const u = new URL(withScheme);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    const path = u.pathname || '';

    if (host === 'digitalmedia.sandia.gov') {
      const playMatch = path.match(/\/Mediasite\/Play\/([a-f0-9]+)/i);
      if (playMatch?.[1]) {
        return `https://digitalmedia.sandia.gov/Mediasite/Play/${playMatch[1]}`;
      }

      const presMatch = path.match(/\/Presentation\/([a-f0-9]+)/i);
      if (presMatch?.[1]) {
        return `https://digitalmedia.sandia.gov/Mediasite/Play/${presMatch[1]}`;
      }
    }

    return withScheme;
  } catch {
    return withScheme;
  }
}

export function getDigitalMediaVideoCode(url) {
  if (!url) return '';
  try {
    const u = new URL(normalizeVideoUrl(url));
    const path = u.pathname || '';

    const playMatch = path.match(/\/Mediasite\/Play\/([a-f0-9]+)/i);
    if (playMatch?.[1]) return playMatch[1];

    const presMatch = path.match(/\/Presentation\/([a-f0-9]+)/i);
    if (presMatch?.[1]) return presMatch[1];

    return '';
  } catch {
    return '';
  }
}

export function getYouTubeId(url) {
  try {
    const u = new URL(url);
    let videoId = '';

    if (u.hostname.includes('youtube.com')) {
      videoId = u.searchParams.get('v') || '';
      if (!videoId && u.pathname.startsWith('/embed/')) {
        videoId = u.pathname.split('/embed/')[1] || '';
      }
    } else if (u.hostname === 'youtu.be') {
      videoId = u.pathname.replace('/', '');
    }
    return videoId || '';
  } catch {
    return '';
  }
}

export function getVimeoId(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    return parts?.[0] || '';
  } catch {
    return '';
  }
}

const vimeoThumbCache = new Map();

export function getYouTubeThumbnail(url) {
  const id = getYouTubeId(url);
  if (!id) return '';
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

export async function getVimeoThumbnail(url, { signal } = {}) {
  if (!url) return '';
  if (vimeoThumbCache.has(url)) return vimeoThumbCache.get(url);

  const endpoint = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;

  const res = await fetch(endpoint, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!res.ok) return '';

  const data = await res.json().catch(() => null);
  const thumb = typeof data?.thumbnail_url === 'string' ? data.thumbnail_url : '';

  vimeoThumbCache.set(url, thumb || '');
  return thumb || '';
}

export async function getThumbnailUrl(url, { signal } = {}) {
  const normalized = normalizeVideoUrl(url);
  const source = getVideoSource(normalized);
  if (!source || !isSupportedVideoSource(source)) return '';

  if (source === 'youtube') return getYouTubeThumbnail(normalized);
  if (source === 'vimeo') return getVimeoThumbnail(normalized, { signal });

  return '';
}

async function fetchAuthToken(videoCode, { signal } = {}) {
  const endpoint = `/wp-json/digitalmedia/v1/auth-token/?video_code=${encodeURIComponent(videoCode)}`;

  const res = await fetch(endpoint, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  });

  const contentType = res.headers.get('content-type') || '';
  const bodyText = await res.text();

  let data;
  try {
    data = JSON.parse(bodyText);
  } catch {
    throw new Error(
      `Auth endpoint returned invalid JSON (HTTP ${res.status}, content-type: ${contentType}). First bytes: ${bodyText.slice(
        0,
        200
      )}`
    );
  }

  if (!res.ok) {
    const msg = data?.message || data?.error || `HTTP ${res.status}`;
    throw new Error(`Auth token request failed: ${msg}`);
  }

  if (typeof data?.authToken !== 'string' || !data.authToken) {
    throw new Error(`Auth token response missing authToken string. Got: ${bodyText.slice(0, 200)}`);
  }

  return data.authToken;
}

export function youtubeEmbed(url) {
  const id = getYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : '';
}

export function vimeoEmbed(url) {
  const id = getVimeoId(url);
  return id ? `https://player.vimeo.com/video/${id}` : '';
}

export async function getEmbedUrl(url, videoCode, { signal } = {}) {
  if (!url) return '';

  const normalized = normalizeVideoUrl(url);
  const source = getVideoSource(normalized);
  if (!source || !isSupportedVideoSource(source)) return '';

  try {
    if (source === 'digitalmedia') {
      const code = videoCode || getDigitalMediaVideoCode(normalized);
      if (!code) return '';

      const authToken = await fetchAuthToken(code, { signal });
      return (
        `https://digitalmedia.sandia.gov/Mediasite/Play/${code}` +
        `?AuthTicket=${encodeURIComponent(authToken)}` +
        `&autostart=false&player=50a54cab1d3541bc913f556ea1b6df590a&playfrom=0&covertitle=false`
      );
    }

    if (source === 'youtube') return youtubeEmbed(normalized);
    if (source === 'vimeo') return vimeoEmbed(normalized);

    if (source === 'digitalops') return normalized;

    // Direct MP4 from any *.sandia.gov host
    if (source === 'sandia-mp4' || source === 'sharepoint-mp4') {
      return normalized;
    }

    return '';
  } catch (e) {
    console.error('getEmbedUrl error:', e);
    return '';
  }
}
